import React, { Component } from "react";
import ReactECharts from 'echarts-for-react';
import "./ReusableGraph.scss";

/**
 * ReusableGraph Component
 * A flexible component for rendering different types of ECharts graphs
 * 
 * Props:
 * @param {Object} graphConfig - Configuration object for the graph
 * @param {string} graphConfig.type - Type of graph: 'line', 'bar', 'pie', 'mixed', etc.
 * @param {Array} graphConfig.data - Data array for the graph
 * @param {Array} graphConfig.xAxisData - X-axis data (for line/bar/mixed charts)
 * @param {Array} graphConfig.series - Series configuration array
 * @param {Object} graphConfig.options - Additional ECharts options (tooltip, legend, grid, etc.)
 * @param {number} height - Height of the graph container (default: 600px)
 * @param {string} className - Additional CSS class name
 */
class ReusableGraph extends Component {
    constructor(props) {
        super(props);
        this.props = props;
    }

    /**
     * Generate ECharts option based on graph type and configuration
     */
    getGraphOption = () => {
        const { graphConfig } = this.props;
        
        if (!graphConfig || !graphConfig.data) {
            return {};
        }

        const { type, data, xAxisData, series, options = {} } = graphConfig;

        // Base configuration
        const baseOption = {
            tooltip: options.tooltip || {
                trigger: 'axis',
                axisPointer: {
                    type: 'cross'
                }
            },
            legend: options.legend || {
                data: series ? series.map(s => s.name) : []
            },
            grid: options.grid || {
                bottom: 100,
                left: 60,
                right: 60,
                top: 60
            },
            ...options
        };

        // Generate option based on graph type
        switch (type) {
            case 'line':
                return this.getLineChartOption(baseOption, data, xAxisData, series);
            
            case 'bar':
                return this.getBarChartOption(baseOption, data, xAxisData, series);
            
            case 'pie':
                return this.getPieChartOption(baseOption, data, series);
            
            case 'mixed':
                return this.getMixedChartOption(baseOption, data, xAxisData, series);
            
            default:
                // If custom series is provided, use it directly
                if (series && Array.isArray(series)) {
                    return {
                        ...baseOption,
                        xAxis: this.getXAxisConfig(xAxisData, options.xAxis),
                        yAxis: this.getYAxisConfig(options.yAxis),
                        series: series
                    };
                }
                return baseOption;
        }
    }

    /**
     * Get X-axis configuration
     */
    getXAxisConfig = (xAxisData, customXAxis = {}) => {
        if (!xAxisData) return null;

        return [{
            type: 'category',
            data: xAxisData,
            axisTick: {
                alignWithLabel: true
            },
            axisLabel: {
                rotate: customXAxis.rotate || 0,
                interval: customXAxis.interval !== undefined ? customXAxis.interval : 'auto',
                ...customXAxis.axisLabel
            },
            ...customXAxis
        }];
    }

    /**
     * Get Y-axis configuration
     */
    getYAxisConfig = (customYAxis = {}) => {
        if (Array.isArray(customYAxis)) {
            return customYAxis;
        }
        
        if (customYAxis && Object.keys(customYAxis).length > 0) {
            return [customYAxis];
        }

        return [{
            type: 'value',
            ...customYAxis
        }];
    }

    /**
     * Generate Line Chart Option
     */
    getLineChartOption = (baseOption, data, xAxisData, series) => {
        const lineSeries = series || [{
            name: 'Line',
            type: 'line',
            data: data,
            smooth: true
        }];

        return {
            ...baseOption,
            xAxis: this.getXAxisConfig(xAxisData, baseOption.xAxis),
            yAxis: this.getYAxisConfig(baseOption.yAxis),
            series: lineSeries
        };
    }

    /**
     * Generate Bar Chart Option
     */
    getBarChartOption = (baseOption, data, xAxisData, series) => {
        const barSeries = series || [{
            name: 'Bar',
            type: 'bar',
            data: data
        }];

        return {
            ...baseOption,
            xAxis: this.getXAxisConfig(xAxisData, baseOption.xAxis),
            yAxis: this.getYAxisConfig(baseOption.yAxis),
            series: barSeries
        };
    }

    /**
     * Generate Pie Chart Option
     */
    getPieChartOption = (baseOption, data, series) => {
        const pieSeries = series || [{
            name: 'Pie',
            type: 'pie',
            radius: '50%',
            data: data,
            emphasis: {
                itemStyle: {
                    shadowBlur: 10,
                    shadowOffsetX: 0,
                    shadowColor: 'rgba(0, 0, 0, 0.5)'
                }
            }
        }];

        return {
            ...baseOption,
            series: pieSeries
        };
    }

    /**
     * Generate Mixed Chart Option (Line + Bar)
     */
    getMixedChartOption = (baseOption, data, xAxisData, series) => {
        if (!series || !Array.isArray(series)) {
            return baseOption;
        }

        return {
            ...baseOption,
            xAxis: this.getXAxisConfig(xAxisData, baseOption.xAxis),
            yAxis: this.getYAxisConfig(baseOption.yAxis),
            series: series
        };
    }

    render() {
        const { graphConfig, height = 600, className = '' } = this.props;

        if (!graphConfig || !graphConfig.data) {
            return (
                <div className={`reusable-graph-container ${className}`}>
                    <div className="no-data-message">No graph data available</div>
                </div>
            );
        }

        const option = this.getGraphOption();

        return (
            <div className={`reusable-graph-container ${className}`}>
                <ReactECharts
                    option={option}
                    style={{ height: `${height}px`, width: '100%' }}
                    opts={{ renderer: 'canvas' }}
                />
            </div>
        );
    }
}

export default ReusableGraph;

